/**
 * Session recording — turning a few seconds in front of the camera into
 * something two people can read afterwards.
 *
 * The format is columnar: one array per signal rather than one object per
 * sample. That is not a micro-optimisation. A recording is read by asking
 * questions like "what did aperture do while they were saying ah", and columnar
 * data answers that with a slice instead of a map over eighteen-key objects. It
 * also compresses far better, because a column of similar floats is exactly what
 * gzip is good at.
 *
 * Marks are the other half. An unlabelled recording tells you what the numbers
 * did; a marked one tells you what the numbers did *while the speaker was doing
 * a specific thing*, which is the only form in which a threshold can be argued
 * about. Phases 5 and 6 need labelled fixtures, and the cheapest moment to
 * label one is while it is being made.
 *
 * Derived intermediates (iod, gap, corner, variance, widthGeom, widthShape) are
 * recorded alongside the contract fields. They are what GAP_FULL and JITTER_FULL
 * get retuned against, and they cannot be recovered from the MouthSample after
 * the fact.
 */

import { config } from './config.js';
import { tuning } from './tunables.js';

export const RECORDING_VERSION = 2;

/** MouthSample fields, in the order they are stored. */
const SAMPLE_FIELDS = [
  't',
  'aperture',
  'width',
  'jawOpen',
  'pucker',
  'funnel',
  'close',
  'press',
  'smile',
  'stretch',
  'lowerDown',
  'lipJitter',
];

/**
 * Derived intermediates, carried on `_raw`. Retuning needs these, and none of
 * them survive into the MouthSample.
 *
 * `lipOffset` in particular is the raw signal rather than a measure of it, so a
 * different jitter measure can be fitted against an existing recording instead
 * of asking for another take.
 */
const RAW_FIELDS = ['iod', 'gap', 'corner', 'lipOffset', 'jitterRaw', 'widthGeom', 'widthShape'];

/** AudioFrame fields, at rAF rate. Version 2 and later. */
const AUDIO_FIELDS = [
  't',
  'rms',
  'db',
  'zcr',
  'flatness',
  'centroid',
  'f0',
  'periodicity',
  'attack',
];

/**
 * The worklet envelope, at ~375 Hz. Two columns and by far the largest thing in
 * the file — around 400 kB of JSON for a 90 second take.
 *
 * Worth every byte. Phase 5 detects trills by FFT-ing this signal, and its
 * whole acceptance criterion is a number measured off a real lip trill. With the
 * envelope recorded, that detector can be written, tuned and regression-tested
 * against a take that already exists. Without it, every adjustment to a
 * threshold costs another afternoon in front of the camera making the same
 * noise, and the two takes are never quite the same noise.
 */
const ENVELOPE_FIELDS = ['t', 'rms'];

/**
 * Time rounds to 0.1 ms — finer than a 30 Hz camera can justify, but cheap, and
 * fixed decimal places are the right rule for a quantity with a fixed scale.
 */
const round = (v, dp) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

/**
 * Everything else rounds to significant figures, not decimal places.
 *
 * The signals here do not share a scale. `aperture` lives in 0..1, but the lip
 * variance that `lipJitter` is derived from sits around 1e-7, and JITTER_FULL —
 * the constant this recording exists to help retune — defaults to 2e-5. Fixed
 * decimal places generous enough for aperture round every one of those to zero,
 * producing a file that looks fine and has silently thrown away the only column
 * anybody wanted.
 */
const sig = (v, digits = 6) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  if (v === 0) return 0;
  return Number(v.toPrecision(digits));
};

export class Recorder {
  constructor() {
    /** @type {?{id: string, t0: number, startedISO: string, columns: Record<string, number[]>, marks: object[], note: string, env: object}} */
    this._session = null;
    /** Finished sessions, newest first. @type {object[]} */
    this.sessions = [];
  }

  get recording() {
    return this._session !== null;
  }

  get sampleCount() {
    return this._session ? this._session.columns.t.length : 0;
  }

  /** Milliseconds since record was pressed. */
  elapsed(now = performance.now()) {
    return this._session ? now - this._session.t0 : 0;
  }

  get marks() {
    return this._session ? this._session.marks : [];
  }

  /**
   * Begin a recording.
   *
   * @param {object} [env] environment worth knowing when reading this back:
   *   camera dimensions, delegate, frame rate, user agent.
   * @param {string} [note]
   */
  start(env = {}, note = '') {
    const t0 = performance.now();
    const columns = {};
    for (const f of [...SAMPLE_FIELDS, ...RAW_FIELDS]) columns[f] = [];
    const audio = {};
    for (const f of AUDIO_FIELDS) audio[f] = [];
    const envelope = {};
    for (const f of ENVELOPE_FIELDS) envelope[f] = [];

    this._session = {
      id: `${new Date().toISOString().replace(/[:.]/g, '-')}`,
      t0,
      startedISO: new Date().toISOString(),
      columns,
      audio,
      envelope,
      marks: [],
      speech: [],
      events: [],
      note,
      env,
      // Which controls were away from their shipped values when record was
      // pressed. Captured here rather than at stop() because it is the tuning
      // the take *began* under; anything moved while it ran is logged as a
      // `tune:` event, with a timestamp, which is the only form that can explain
      // a detector that behaved differently in the second half.
      //
      // A diff, so a take at defaults says so in an empty object.
      tuning: tuning(),
    };
    return this._session.id;
  }

  /**
   * Record one sample. Called from the landmarker's frame callback rather than
   * polled off the ring buffer, so nothing is dropped when the buffer wraps or
   * the render loop stutters.
   *
   * Times are stored relative to the start of the recording. Absolute
   * `performance.now()` values are meaningless once the page has closed, and a
   * relative timeline is what lets two recordings be laid side by side.
   *
   * @param {import('./types.js').MouthSample} sample
   */
  push(sample) {
    if (!this._session) return;
    const c = this._session.columns;
    c.t.push(round(sample.t - this._session.t0, 1));
    for (const f of SAMPLE_FIELDS) {
      if (f !== 't') c[f].push(sig(sample[f]));
    }
    const raw = sample._raw ?? {};
    for (const f of RAW_FIELDS) c[f].push(sig(raw[f]));
  }

  /**
   * Record one rAF-rate audio feature frame.
   *
   * @param {import('./types.js').AudioFrame} frame
   */
  pushAudio(frame) {
    if (!this._session) return;
    const c = this._session.audio;
    c.t.push(round(frame.t - this._session.t0, 1));
    for (const f of AUDIO_FIELDS) {
      if (f !== 't') c[f].push(sig(frame[f]));
    }
  }

  /**
   * Record one envelope sample, straight off the worklet.
   *
   * @param {import('./types.js').EnvelopeSample} sample
   */
  pushEnvelope(sample) {
    if (!this._session) return;
    const c = this._session.envelope;
    c.t.push(round(sample.t - this._session.t0, 1));
    c.rms.push(sig(sample.rms));
  }

  get audioCount() {
    return this._session ? this._session.audio.t.length : 0;
  }

  get envelopeCount() {
    return this._session ? this._session.envelope.t.length : 0;
  }

  /**
   * Label the moment. Stored as a point in time, not a span: spans invite
   * arguing about where exactly the "ah" started, and two marks make a span
   * whenever one is genuinely needed.
   *
   * @param {string} label
   * @param {number} [at] performance.now(); defaults to now
   */
  mark(label, at = performance.now()) {
    if (!this._session) return null;
    const m = { t: round(at - this._session.t0, 1), label };
    this._session.marks.push(m);
    return m;
  }

  /**
   * Record one recogniser result and what the diff did with it.
   *
   * Every result, including the ones that were immediately revised away. The
   * revisions are the interesting part: a bug in the prefix diff shows up as a
   * `kept` that drops when it should not have, and that is invisible once only
   * the final text survives.
   *
   * @param {object} o
   * @param {string} o.text
   * @param {boolean} o.isFinal
   * @param {number} o.at            performance.now() when the result arrived
   * @param {object} o.diff          what Transcript.update returned
   */
  speech({ text, isFinal, at, diff }) {
    if (!this._session) return;
    // Named explicitly rather than spread: `diff` also carries an absolute
    // `at`, and letting it through would put a raw performance.now() into a
    // document whose whole timebase is relative to the recording.
    const { kept, added, dropped, finalised } = diff ?? {};
    this._session.speech.push({
      t: round(at - this._session.t0, 1),
      text,
      isFinal: Boolean(isFinal),
      kept,
      added,
      dropped,
      finalised,
    });
  }

  /**
   * Record something the app did, as opposed to something the speaker did.
   *
   * Kept apart from `marks` because marks are labels for sounds, and the
   * analyzer iterates their distinct values as the set of things being
   * measured — a "camera-stopped" in there would become a phantom vowel.
   *
   * The case this exists for: stopping the camera mid-recording leaves a tail
   * of duration with no samples behind it. Perfectly legitimate when the take
   * is about speech, and completely baffling later without a note saying so.
   *
   * @param {string} name
   * @param {number} [at]
   */
  event(name, at = performance.now()) {
    if (!this._session) return;
    this._session.events.push({ t: round(at - this._session.t0, 1), name });
  }

  /** Note field can be edited while recording. */
  setNote(note) {
    if (this._session) this._session.note = note;
  }

  /**
   * Revise the environment snapshot mid-recording.
   *
   * `start()` captures the environment as it is at the moment record is
   * pressed, and some of it is not known yet. Pressing "record everything"
   * starts the microphone and the recording together, so the first worklet
   * batch — which is what reveals the block size and therefore the envelope
   * rate — has not arrived. A recording made that way stored
   * `blockSize: 0, envelopeRate: 0`, and every tool that reads it had to fall
   * back to assuming 375 Hz. Assuming the envelope rate is the one thing this
   * project has said repeatedly it will not do.
   *
   * @param {object} patch merged over the existing env
   */
  updateEnv(patch) {
    if (!this._session) return;
    this._session.env = { ...this._session.env, ...patch };
  }

  /**
   * Finish, and return the session as the object that gets written to disk or
   * to R2. Also kept in `sessions` so the page can re-download or re-upload it
   * without a second take.
   */
  stop() {
    if (!this._session) return null;
    const s = this._session;
    this._session = null;

    const session = {
      version: RECORDING_VERSION,
      id: s.id,
      startedISO: s.startedISO,
      durationMs: round(performance.now() - s.t0, 1),
      note: s.note,
      env: s.env,
      // A snapshot, so a recording can be read against the constants that were
      // in force when it was made. Retuning these is the whole point of
      // recording, and a fixture that does not say what it was tuned against is
      // a fixture you cannot trust six weeks later.
      //
      // `config` stayed vision-only when audio arrived rather than becoming
      // everything: the analyzer and every recording made before Phase 4 read
      // that key as the vision constants, and widening it would have quietly
      // changed what an existing file means.
      config: structuredClone(config.vision),
      audioConfig: structuredClone(config.audio),
      // Every Phase 8 control that was away from its default when this started.
      // The two snapshots above are whole subtrees and predate the sliders; this
      // is the one that answers "was anything moved", across all of config, in a
      // line rather than in a diff of two hundred numbers.
      tuning: s.tuning,
      sampleRateHz: s.columns.t.length > 1 ? round(estimateRate(s.columns.t), 2) : 0,
      audioRateHz: s.audio.t.length > 1 ? round(estimateRate(s.audio.t), 2) : 0,
      envelopeRateHz: s.envelope.t.length > 1 ? round(estimateRate(s.envelope.t), 2) : 0,
      fields: [...SAMPLE_FIELDS, ...RAW_FIELDS],
      audioFields: AUDIO_FIELDS,
      envelopeFields: ENVELOPE_FIELDS,
      marks: s.marks,
      // App-level events: camera on/off, listening on/off. What explains a
      // stretch of recording with no samples behind it.
      events: s.events,
      // Every recogniser result, revisions included. See speech() for why the
      // ones that were immediately overwritten are the ones worth keeping.
      speech: s.speech,
      columns: s.columns,
      audio: s.audio,
      envelope: s.envelope,
    };

    this.sessions.unshift(session);
    return session;
  }

  /** Throw away the in-progress recording. */
  cancel() {
    this._session = null;
  }
}

/** Median-interval sample rate — robust to the odd dropped frame. */
function estimateRate(t) {
  const gaps = [];
  for (let i = 1; i < t.length; i++) gaps.push(t[i] - t[i - 1]);
  gaps.sort((a, b) => a - b);
  const median = gaps[gaps.length >> 1];
  return median > 0 ? 1000 / median : 0;
}

/**
 * Filename for a session.
 *
 * This must produce exactly what `functions/api/sessions.js` produces for the
 * same recording. It did not: an earlier version substituted the word "session"
 * when the note was empty, while the server left the slug off entirely — so a
 * take that was both uploaded and downloaded ended up as
 * `<id>.json` in R2 and `<id>_session.json` on disk, and the two looked like
 * two different recordings. The server is the authority on the key; this
 * mirrors it.
 */
export function sessionKey(session) {
  const slug = String(session.note ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `${session.id}${slug ? `_${slug}` : ''}.json`;
}

export default Recorder;
